import React, {createContext, PropsWithChildren, useMemo, useRef, useState} from "react";
import {Opening} from "../../../../api/Openings";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {Button, Card, Popper} from "@mui/material";
import {orange} from "@mui/material/colors";
import { PopperProps } from '@mui/material/Popper';
import {errShowNoSnackInTable} from "../../../../misc/scroller/EditProvider";
import {BulkUpdateOpenings} from "../../shopdrawing/hardware/bulkUpdateOpenings/BulkUpdateOpenings";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {useAlternative} from "../alternative/Alternative";
import {useTableRefreshAll} from "../../../../misc/scroller/Table";

export const BreakHardwareLinkContext = createContext({
    onError: async <T extends (Opening | QuoteOpening)>(obj: T, e: Error, onRetry: (input: T) => Promise<any>): Promise<any> => {
        throw e;
    }
})

type ShowInfo = {
    error: string;
    opening: Opening|QuoteOpening;
    cancel(): void;
    approve(): void;
}

export function BreakHardwareLinkProvider(props: PropsWithChildren<{}>) {
    const [show, setShow] = useState<ShowInfo|null>(null)
    const hwGroup = useRef<number>();
    const [showEditOpeningsForGroup, setShowEditOpeningsForGroup] = useState<number>();
    const project = useProjectId();
    const alternative = useAlternative();
    const showingRef = useRef(!!show);
    showingRef.current = !!show;

    const ctx = useMemo(() => {
        return {
            onError: async <T extends (Opening | QuoteOpening)>(obj: T, e: Error, onRetry: (input: T) => Promise<any>) => {
                if(e.toString().toLowerCase().indexOf("hardware group") === -1) {
                    return Promise.reject(e);
                }

                // con only show one at a time.
                if(showingRef.current) {
                    return Promise.reject(e);
                }

                showingRef.current = true;

                return new Promise<any>((resolve, reject) => {
                    setAnchorEl({
                        getBoundingClientRect: () => {
                            const body = document.body.getBoundingClientRect();
                            const bottom = 50;
                            const left = 10;

                            return {
                                top: body.top + body.height - bottom,
                                left: body.left + left,
                                right: body.right + left,
                                bottom: bottom,
                                width: 1,
                                height: 1,
                            } as DOMRect;
                        }
                    });

                    hwGroup.current = obj.hardwareGroup;

                   setShow({
                          error: e.toString(),
                          opening: obj,
                          cancel: () => {
                            reject(new Error(errShowNoSnackInTable));
                          },
                          approve: async () => {
                              try {
                                  const updated = Object.assign({}, obj, {
                                      hardwareGroup: null,
                                      unlinkFromConflictingHardwareGroup: true,
                                  })

                                  const result = await onRetry(updated);
                                  resolve(result);
                              } catch (e) {
                                  reject(e);
                              }
                          }
                   })
                });
            }
        }
    }, []);

    const [anchorEl, setAnchorEl] = React.useState<PopperProps['anchorEl']>(null);
    const refreshAll = useTableRefreshAll();

    return (
        <BreakHardwareLinkContext.Provider value={ctx}>
            {showEditOpeningsForGroup ? null : props.children}
            {show && <Popper open placement="bottom-start"
                             anchorEl={anchorEl}
                             style={{zIndex: 14}}
            >

                <Card style={{padding: 16, backgroundColor: orange["600"], zIndex: 1000, color: "white", fontWeight: "500"}}>
                    <div>
                        {show.error}
                    </div>

                    <div style={{display: "flex", justifyContent: "flex-end"}}>
                        <Button style={{color: "white"}} onClick={() => {
                            show?.cancel();
                            setShow(null);
                        }}>Ignore</Button>
                        <Button style={{color: "white"}} onClick={() => {
                            show?.approve();
                            setShow(null);
                        }}>Unlink Hardware Group</Button>
                        <Button style={{color: "white"}} onClick={() => {
                            show?.cancel();
                            setShow(null);

                            if(hwGroup.current) setShowEditOpeningsForGroup(hwGroup.current);
                        }}>Edit Group Openings</Button>
                    </div>
                </Card>
            </Popper>}
            {showEditOpeningsForGroup &&
                <BulkUpdateOpenings
                    alternative={alternative}
                    project={project}
                    hwGroup={showEditOpeningsForGroup}
                    onUpdated={() => {
                        refreshAll();
                        setShowEditOpeningsForGroup(undefined);
                    }}
                    onClose={() => setShowEditOpeningsForGroup(undefined)}
            />}
        </BreakHardwareLinkContext.Provider>
    )
}